import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, 
  BookOpen,
  HelpCircle,
  Apple,
  Leaf,
  Clock
} from 'lucide-react';

export default function RuchiBoxDigest() {
  const navigate = useNavigate();

  const tiles = [
    {
      id: 'faq',
      title: 'FAQ',
      description: 'Answers to commonly asked questions',
      icon: HelpCircle,
      color: 'from-blue-500 to-indigo-500'
    },
    {
      id: 'balanced-diet',
      title: 'Balanced Diet',
      description: 'Tips for healthy eating habits',
      icon: Apple,
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 'home-remedies',
      title: 'Home Remedies',
      description: 'Traditional wellness solutions',
      icon: Leaf,
      color: 'from-amber-500 to-orange-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="pl-0"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold">RuchiBox Digest</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
            <BookOpen className="w-12 h-12 text-white" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-foreground mb-4">
            Knowledge Hub
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Your source for helpful information about food, health, and wellness. 
            Learn from our community's collective wisdom!
          </p>
        </div>

        {/* Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {tiles.map((tile) => (
            <Card 
              key={tile.id}
              className="overflow-hidden cursor-pointer card-hover group"
              data-testid={`digest-tile-${tile.id}`}
            >
              <div className={`h-32 bg-gradient-to-br ${tile.color} flex items-center justify-center`}>
                <tile.icon className="w-16 h-16 text-white opacity-90 group-hover:scale-110 transition-transform" />
              </div>
              <CardContent className="p-6 text-center">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  {tile.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {tile.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Coming Soon */}
        <Card className="border-2 border-dashed border-teal-200 bg-teal-50/50">
          <CardContent className="p-12 text-center">
            <Clock className="w-16 h-16 text-teal-300 mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-bold text-foreground mb-3">
              Coming Soon!
            </h3>
            <p className="text-muted-foreground max-w-md mx-auto mb-6">
              Our content team is curating valuable articles and tips. 
              Check back soon for helpful guides on nutrition and wellness!
            </p>
            <Button onClick={() => navigate('/auth')} className="rounded-full bg-teal-500 hover:bg-teal-600">
              Sign Up to Get Notified
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
